import clamp from "lodash/clamp";
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from "react";

import { FLOATING_EXPANDABLE_BUBBLE_MARGIN, FLOATING_EXPANDABLE_BUBBLE_NAVBAR_WIDTH } from "../constants";
import type { PanelSize } from "../floating_position_store/floating_position_store";
import {
  useFloatingEntry,
  useFloatingPositionStoreActions
} from "../floating_position_store/floating_position_store";
import type { ResizeDirection } from "../parts/get_resize_direction";
import { computeResizeDelta } from "./use_floating_resize.utils";

export type UseFloatingResizeOptions = {
  storageKey: string;
  defaultSize: PanelSize;
  minSize: PanelSize;
  maxSize: PanelSize;
  enabled: boolean;
};

export type DragPosition = { top: number; left: number };

export type UseFloatingResizeResult = {
  size: PanelSize;
  isResizing: boolean;
  dragPosition: DragPosition | null;
  /** Call from the panel wrapper's onMouseDown when a resize direction is detected. */
  onResizeMouseDown: (event: ReactMouseEvent<HTMLElement>, direction: ResizeDirection) => void;
};

export const useFloatingResize = ({
  storageKey,
  defaultSize,
  minSize,
  maxSize,
  enabled
}: UseFloatingResizeOptions): UseFloatingResizeResult => {
  const { size: persistedSize } = useFloatingEntry(storageKey);
  const { setSize } = useFloatingPositionStoreActions();

  const [isResizing, setIsResizing] = useState(false);
  const [dragSize, setDragSize] = useState<PanelSize | null>(null);
  const [dragPosition, setDragPosition] = useState<DragPosition | null>(null);

  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startWidthRef = useRef(0);
  const startHeightRef = useRef(0);
  const startTopRef = useRef(0);
  const startLeftRef = useRef(0);
  const directionRef = useRef<ResizeDirection | null>(null);

  const persistedOrDefault = persistedSize ?? defaultSize;
  const constrainedSize = {
    width: clamp(persistedOrDefault.width, minSize.width, maxSize.width),
    height: clamp(persistedOrDefault.height, minSize.height, maxSize.height)
  };
  const size = dragSize ?? constrainedSize;

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const direction = directionRef.current;
      if (!direction) {
        return;
      }

      const dx = event.clientX - startXRef.current;
      const dy = event.clientY - startYRef.current;

      const viewportBounds = {
        minLeft: FLOATING_EXPANDABLE_BUBBLE_NAVBAR_WIDTH + FLOATING_EXPANDABLE_BUBBLE_MARGIN,
        maxRight: window.innerWidth - FLOATING_EXPANDABLE_BUBBLE_MARGIN,
        minTop: FLOATING_EXPANDABLE_BUBBLE_MARGIN,
        maxBottom: window.innerHeight - FLOATING_EXPANDABLE_BUBBLE_MARGIN
      };

      const { newWidth, newHeight, newLeft, newTop } = computeResizeDelta(
        direction,
        dx,
        dy,
        startWidthRef.current,
        startHeightRef.current,
        startTopRef.current,
        startLeftRef.current,
        minSize,
        maxSize,
        viewportBounds
      );

      setDragSize({ width: newWidth, height: newHeight });

      if (newLeft !== null || newTop !== null) {
        setDragPosition({
          top: newTop ?? startTopRef.current,
          left: newLeft ?? startLeftRef.current
        });
      }
    },
    [minSize, maxSize]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);

    setDragSize((currentDragSize) => {
      if (currentDragSize !== null) {
        setSize(storageKey, currentDragSize);
      }
      return null;
    });

    setDragPosition((currentDragPosition) => {
      if (currentDragPosition !== null) {
        window.dispatchEvent(
          new CustomEvent("floating-resize-end", {
            detail: { storageKey, dragPosition: currentDragPosition }
          })
        );
      }
      return null;
    });

    directionRef.current = null;
  }, [storageKey, setSize]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const onResizeMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLElement>, direction: ResizeDirection) => {
      if (!enabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const rect = event.currentTarget.getBoundingClientRect();

      directionRef.current = direction;
      startXRef.current = event.clientX;
      startYRef.current = event.clientY;
      startWidthRef.current = size.width;
      startHeightRef.current = size.height;
      startTopRef.current = rect.top;
      startLeftRef.current = rect.left;

      setIsResizing(true);
    },
    [enabled, size]
  );

  return {
    size,
    isResizing,
    dragPosition,
    onResizeMouseDown
  };
};
